/*
 *  -----------------------------------------------------------------------
 *    <copyright file="schedule-bar/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import {
    SVTCheckIdsExist,
    SVTCheckUniqueIds,
    SVTCheckUniqueTAAndCohortCombination,
    SVTFilterFilledSchedules,
    SVTGetActiveSchedules,
} from '@components/admin/schedule-versions-grid/schedule-bar/schedule-bar.helper';
import DialogInformationBox from '@components/core/dialog-information-box/dialog-information-box';
import {
    InfoBoxIcons,
    InfoBoxStyleClasses,
} from '@components/core/dialog-information-box/dialog-information-box.interface';
import GridHeader from '@components/core/grid-header';
import GridMenu from '@components/core/grid-header/grid-menu';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import { ScheduleBarProps, SVTInfoMessageType } from './schedule-bar.interface';
import { useTranslation } from '@hooks/index';

import './styles.scss';
import { SvtDependencyType } from '@types/graphql-global-types';

const ScheduleBar = ({ groupedVersions, openAddSchedules, openImportAndTRSetup, canEdit, study }: ScheduleBarProps) => {
    const { t } = useTranslation();

    const [infoBoxText, setInfoBoxText] = useState<{ type: SVTInfoMessageType; text: string }>({
        type: SVTInfoMessageType.information,
        text: '',
    });

    const svtAssociation = study?.svtDependency;
    const schedulesCount = Object.keys(groupedVersions).length;
    const activeSchedules = SVTGetActiveSchedules(groupedVersions || []);

    const tileBoth = () => {
        const filledSchedules = SVTFilterFilledSchedules(activeSchedules);

        const isOneActiveAndFilled =
            activeSchedules.length === 1 &&
            activeSchedules[0].cohortIds?.length &&
            activeSchedules[0].treatmentArmIds?.length;
        const isMultipleActiveAndUnique =
            filledSchedules.length > 1 && SVTCheckUniqueTAAndCohortCombination(filledSchedules);

        if (!activeSchedules.length || isOneActiveAndFilled || isMultipleActiveAndUnique) {
            setInfoBoxText({
                type: SVTInfoMessageType.information,
                text: 'admin:bothAndActiveSchedulesNotRequireActions',
            });
        } else {
            setInfoBoxText({ type: SVTInfoMessageType.warning, text: 'admin:bothAndActiveSchedulesRequireActions' });
        }
    };

    const tileNone = () => {
        if (activeSchedules.length <= 1) {
            setInfoBoxText({
                type: SVTInfoMessageType.information,
                text: 'admin:noneAndActiveSchedulesNotRequireActions',
            });
        } else {
            setInfoBoxText({ type: SVTInfoMessageType.warning, text: 'admin:noneAndActiveSchedulesRequireActions' });
        }
    };

    const tileCohorts = () => {
        const isOneActiveAndHasCohorts = activeSchedules.length === 1 && activeSchedules[0].cohortIds?.length;
        const isMultipleActiveAndUniqueCohorts =
            activeSchedules.length > 1 &&
            SVTCheckIdsExist(activeSchedules, 'cohortIds') &&
            SVTCheckUniqueIds(activeSchedules, 'cohortIds');

        if (!activeSchedules.length || isOneActiveAndHasCohorts || isMultipleActiveAndUniqueCohorts) {
            setInfoBoxText({
                type: SVTInfoMessageType.information,
                text: 'admin:cohortsAndActiveSchedulesNotRequireActions',
            });
        } else {
            setInfoBoxText({ type: SVTInfoMessageType.warning, text: 'admin:cohortsAndActiveSchedulesRequireActions' });
        }
    };

    const tileTA = () => {
        const isOneActiveAndHasTA = activeSchedules.length === 1 && activeSchedules[0].treatmentArmIds?.length;
        const isMultipleActiveAndUniqueTA =
            activeSchedules.length > 1 &&
            SVTCheckIdsExist(activeSchedules, 'treatmentArmIds') &&
            SVTCheckUniqueIds(activeSchedules, 'treatmentArmIds');

        if (!activeSchedules.length || isOneActiveAndHasTA || isMultipleActiveAndUniqueTA) {
            setInfoBoxText({
                type: SVTInfoMessageType.information,
                text: 'admin:TAAndActiveSchedulesNotRequireActions',
            });
        } else {
            setInfoBoxText({ type: SVTInfoMessageType.warning, text: 'admin:TAAndActiveSchedulesRequireActions' });
        }
    };

    const defineWarningInfoTile = (associationName: SvtDependencyType | null) => {
        switch (associationName) {
            case SvtDependencyType.BOTH:
                tileBoth();
                break;
            case SvtDependencyType.NONE:
                tileNone();
                break;
            case SvtDependencyType.COHORTS:
                tileCohorts();
                break;
            case SvtDependencyType.TREATMENT_ARMS:
                tileTA();
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (groupedVersions && Object.keys(groupedVersions).length) {
            defineWarningInfoTile(svtAssociation);
        }
    }, [svtAssociation, groupedVersions]);

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.expandMenu,
            customClassName: 'grid-header__item--always-visible',
            expandMenuItems: [
                {
                    label: 'admin:importAndTRSetup',
                    onClick: () => openImportAndTRSetup(),
                    icon: ['fal', 'edit'],
                    enabled: canEdit,
                },
                {
                    label: 'admin:addSchedule',
                    onClick: () => openAddSchedules(),
                    icon: ['fal', 'add'],
                    enabled: canEdit,
                },
            ],
        },
    ];

    const infoBoxClass =
        infoBoxText.type === SVTInfoMessageType.information ? InfoBoxStyleClasses.green : InfoBoxStyleClasses.amber;
    const infoBoxIcon =
        infoBoxText.type === SVTInfoMessageType.information
            ? InfoBoxIcons.infoCircle
            : InfoBoxIcons.exclamationTriangle;

    return (
        <div className="schedules-bar mb-10">
            {!!schedulesCount && (
                <div className="mb-10">
                    <DialogInformationBox
                        topText={t(infoBoxText.text)}
                        boxStyleClass={infoBoxClass}
                        icon={infoBoxIcon}
                    />
                </div>
            )}
            <GridHeader
                menu={<GridMenu extraMenuItems={extraMenuItems} hideFilter />}
                pager={false}
                title={t('admin:visitSchedulesTitle')}
                itemsName={t('admin:scheduleS')}
                totalRecords={schedulesCount}
            />
        </div>
    );
};

export default ScheduleBar;
